import React, { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { Service, Meta } from 'model/Service';
import TransportationServiceRow from './TransportationServiceRow';
import DefaultTableEmptyState from 'components/UI/V3/Table/EmptyState/DefaultTableEmptyState';
import Spinner from 'components/UI/Spinner';
import { DEFAULT_PAGE_SIZE } from 'hooks/useTransportationServices';
import styles from './TransportationServicesTable.module.scss';

interface TransportationServicesTableProps {
    services: Service[];
    meta: Meta | null;
    isLoading?: boolean;
    hasInitialized?: boolean;
    isEmpty?: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    onRetry?: () => void;
    className?: string;
}

const TransportationServicesTable: FunctionComponent<TransportationServicesTableProps> = ({
    services,
    meta,
    isLoading,
    hasInitialized,
    isEmpty,
    onEdit,
    onDuplicate,
    onDeactivate,
    onDelete,
    onPageChange,
    onPageSizeChange,
    onRetry,
    className
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const shouldShowPagination = meta && (meta.currentPage > 1 || meta.totalServices > DEFAULT_PAGE_SIZE);

    const pagination = shouldShowPagination
        ? {
              page: meta!.currentPage,
              totalPages: meta!.totalPages,
              size: meta!.perPage,
              isLoading: isLoading || false
          }
        : undefined;

    const renderTableContent = () => {
        const shouldShowSpinnerInBody = isLoading && (!hasInitialized || services.length === 0);

        if (shouldShowSpinnerInBody) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '40px' }}
                        >
                            <Spinner />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        if (isEmpty) {
            return (
                <TableBody>
                    <DefaultTableEmptyState />
                </TableBody>
            );
        }

        return (
            <TableBody>
                {services.map((service, index) => (
                    <TransportationServiceRow
                        key={service.id}
                        service={service}
                        isLast={services.length === index + 1}
                        isMobile={isMobile}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onDeactivate={onDeactivate}
                        onDelete={onDelete}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onPageSizeChange}
            className={`${styles.table} ${className || ''}`}
            fullHeight
        >
            {!isMobile && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Staff</TableHeaderCell>
                    <TableHeaderCell>Pickup Price</TableHeaderCell>
                    <TableHeaderCell>Dropoff Price</TableHeaderCell>
                    <TableHeaderCell>Taxes</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderTableContent()}
        </Table>
    );
};

export default TransportationServicesTable;
